import { useState } from 'react';
import { Col, FormGroup, Input, Label, Row } from 'reactstrap';
import ACHPaymentForm from '@/components/ACHPaymentForm';
import ButtonRow from '@/components/ButtonRow';
import CreditCardField from '@/components/CreditCardField';
import FlashMessage from '@/components/FlashMessage';
import FormInput from '@/components/FormInput';
import { LegalAgreementCheckbox } from '@/components/LegalAgreementCheckbox';
import PaymentMethodTabs from '@/components/PaymentMethodTabs';
import SubmitButton from '@/components/SubmitButton';
import TwoColumnCard from '@/components/TwoColumnCard';

export interface BillingDetailsCardProps {
  readonly paymentCard: usaepay.PaymentCard;
  readonly cardError: string | null;
  readonly isBusy: boolean;
  readonly activeTab: 'card' | 'ach';
  readonly onTabChange: (tab: 'card' | 'ach') => void;
  readonly achEnabled: boolean;
}

export default function BillingDetailsCard({
  paymentCard,
  cardError,
  isBusy,
  activeTab,
  onTabChange,
  achEnabled,
}: BillingDetailsCardProps) {
  const [legalAccepted, setLegalAccepted] = useState(false);

  return (
    <TwoColumnCard title="Enter Billing Details">
      <FlashMessage className="mb-3" />

      <input type="hidden" name="paymentMethod" value={activeTab} />

      <Row>
        <Col md={6}>
          <FormInput name="firstName" label="First Name" required />
        </Col>

        <Col md={6}>
          <FormInput name="lastName" label="Last Name" required />
        </Col>

        <Col xs={12}>
          <FormInput name="street" label="Street Address" required />
        </Col>

        <Col xs={12}>
          <FormInput name="street2" label="Apt/Suite" />
        </Col>

        <Col md={6}>
          <FormInput name="city" label="City" required />
        </Col>

        <Col md={3}>
          <FormInput name="state" label="State" required />
        </Col>

        <Col md={3}>
          <FormInput name="postalCode" label="Zip Code" required />
        </Col>

        <Col xs={12}>
          <FormInput name="phone" label="Phone Number" type="tel" />
        </Col>

        <Col xs={12}>
          <FormInput name="email" label="Email Address" type="email" />
        </Col>

        <Col xs={12}>
          {achEnabled ? (
            <PaymentMethodTabs
              activeTab={activeTab}
              onTabChange={onTabChange}
              cardContent={
                <CreditCardField
                  paymentCard={paymentCard}
                  errorMessage={cardError}
                />
              }
              achContent={<ACHPaymentForm />}
            />
          ) : (
            <CreditCardField
              paymentCard={paymentCard}
              errorMessage={cardError}
            />
          )}
        </Col>

        <Col xs={12}>
          <FormGroup check className="mt-2 mb-4">
            <Input
              type="checkbox"
              name="sendReceipt"
              id="sendReceipt"
              defaultChecked
            />
            <Label for="sendReceipt" check>
              Email me a receipt
            </Label>
          </FormGroup>
        </Col>

        <Col xs={12}>
          <LegalAgreementCheckbox
            className="mt-2 mb-4"
            legalAccepted={legalAccepted}
            setLegalAccepted={setLegalAccepted}
          />
        </Col>

        <ButtonRow className="mt-3">
          <SubmitButton busy={isBusy} color="success" disabled={!legalAccepted}>
            Submit Payment
          </SubmitButton>
        </ButtonRow>
      </Row>
    </TwoColumnCard>
  );
}
